/**
 * VendorOrders
 *
 * Purpose:
 * - Unified orders page for Purchase Orders and Work Orders.
 * - Provides client-side filtering, action menus, and navigation preserving returnTab.
 *
 * Optimization:
 * - Refactored complex monolithic state into multiple specific state hooks.
 * - Used useCallback and useMemo for performance optimization.
 * - Consolidated data loading logic.
 *
 * Fixes:
 * 1. API Call Loop Prevention: Separated count fetching into dedicated callbacks (loadPOCounts, loadWOCounts) 
 * that use functional state updates, breaking the dependency loop and preventing repeated list fetching.
 * 2. Work Order Tab Switching: Corrected logic in handleSubTabChange and updateURL to ensure the 'subTab' 
 * query parameter and state are correctly set for Work Orders, enabling proper switching between Active/Inactive lists.
 */
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
// External dependencies (Assumed available in the hosting environment: react-redux, moment, idx)
import { useDispatch, useSelector } from "react-redux";
// UI components/libraries
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import Popover from 'react-bootstrap/Popover';
import { Link } from "react-router-dom";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
// External library
import moment from "moment";
import { format } from "date-fns";
import idx from "idx";

// Mocking local component/utility/action imports (Paths must exist in the real project)
import CustomTable from '../../components/table/table';
import DownloadDialog from '../../components/download-dialog';
import DeleteDialog from '../../components/delete-dialog';
import PayInvoice from '../vendor-payment/pay-invoice';
import POFilter from "../suppliers/purchase-order/po-list-filter";
import WOFilter from "../contractors/work-order/wo-list-filter";

import Modal from '../../components/modal/modal';
import { VENDOR, VENDOR_ORDERS, VENDOR_INVOICES, PROJECT_MANAGEMENT, VENDOR_PAYMENT } from "../../constants/route-paths";
import { getPOList, deletePurchaseOrder, downloadProjectPO, getProjectList, getPurchaseOrderTabCounts, getWorkOrderTabCounts, cancelWorkOrder, reactivateWorkOrder } from '../../actions/project-management.action';
import { getGlobalSettingTemplateTypes } from '../../actions/global-setting-template.action';
import { errorSnackBar } from '../../actions/common.action';
import { numberFormat } from "../../util/currency-formatter";
import { PerPageCount } from "../../util/enums";
import { VENDOR_TAB } from "src/constants/vendor.contant";

import './vendor.scss';


// Utility for safely extracting nested properties
const safeIdx = (obj, accessor) => idx(obj, accessor) || '';

function VendorOrders({ rSupplierPermission, wSupplierPermission, rContractorPermission, wContractorPermission, writePermission, projectModule }) {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const entitlements = useSelector(state => state.user.entitlements);

  // --- Core Navigation State Logic ---
  const getTabState = useCallback(() => {
    let activeTab = searchParams.get('tab');
    let activeSubTab = searchParams.get('subTab') || 'active';

    // Determine default tab based on permissions if URL tab is invalid or missing
    if (!activeTab || (activeTab !== VENDOR_TAB.PURCHASE_ORDERS && activeTab !== VENDOR_TAB.WORK_ORDERS)) {
      activeTab = rSupplierPermission ? VENDOR_TAB.PURCHASE_ORDERS : VENDOR_TAB.WORK_ORDERS;
    }

    // Ensure user has permission for the resulting tab
    if (activeTab === VENDOR_TAB.PURCHASE_ORDERS && !rSupplierPermission) {
      activeTab = VENDOR_TAB.WORK_ORDERS;
    } else if (activeTab === VENDOR_TAB.WORK_ORDERS && !rContractorPermission) {
      activeTab = VENDOR_TAB.PURCHASE_ORDERS;
    }

    // If the active tab is WO and no subTab is explicitly in the URL, default to 'active'
    if (activeTab === VENDOR_TAB.WORK_ORDERS && !searchParams.get('subTab')) {
      activeSubTab = 'active';
    }

    return { activeTab, activeSubTab };
  }, [searchParams, rSupplierPermission, rContractorPermission]);

  const initialTabState = getTabState();
  const [activeTab, setActiveTab] = useState(initialTabState.activeTab);
  const [activeSubTab, setActiveSubTab] = useState(initialTabState.activeSubTab);
  const [selectedProject, setSelectedProject] = useState('all');

  // --- Data & Pagination State ---
  const [poData, setPoData] = useState({ list: [], counts: { active: 0, inactive: 0 } });
  const [woData, setWoData] = useState({ list: [], counts: { active: 0, inactive: 0 } });
  const [projectsList, setProjectsList] = useState([{ key: "All Projects", value: "all" }]);
  const [limit, setLimit] = useState(PerPageCount.DEFAULT);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // --- Modal & Action State ---
  const [dialogState, setDialogState] = useState({
    poId: null,
    woNo: '',
    showPayInvoice: false,
    openDeleteDialog: false,
    openCancelDialog: false,
    openReactivateDialog: false,
    downloadData: { open: false, id: "", template: "" },
    templateOptions: [],
    showFilter: false,
    invoiceData: {},
  });

  // --- State Handlers ---

  const updateURL = useCallback((tab, subTab) => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('tab', tab);
    // CRITICAL FIX: Always set/update subTab for persistent state, regardless of tab type.
    if (subTab) {
      newSearchParams.set('subTab', subTab);
    } else {
      newSearchParams.delete('subTab');
    }
    navigate(`${location.pathname}?${newSearchParams.toString()}`, { replace: true });
  }, [navigate, location.pathname, searchParams]);

  const handleMainTabChange = useCallback((key) => {
    setActiveTab(key);
    setActiveSubTab('active'); // Reset to active subtab when switching main tabs
    setSelectedProject('all'); // Reset filter
    setCurrentPage(1);
    setSearch('');
    updateURL(key, 'active');
  }, [updateURL]);

  const handleSubTabChange = useCallback((key) => {
    setActiveSubTab(key);
    setCurrentPage(1);
    // CRITICAL FIX: Ensure updateURL is called to set the 'subTab' query parameter
    updateURL(activeTab, key);
  }, [activeTab, updateURL]);

  const pageHandler = useCallback((page) => {
    setCurrentPage(page > 0 ? page : 1);
  }, []);

  const limitChangeHandler = useCallback((newLimit) => {
    setLimit(newLimit);
    setCurrentPage(1);
  }, []);

  const searchHandler = useCallback((value) => {
    if (value !== search) {
      setSearch(value);
      setCurrentPage(1);
    }
  }, [search]);

  // --- Data Formatters ---

  const formatPurchaseOrderData = useCallback((orders) => {
    return orders.map((po) => {
      const vendorName = safeIdx(po, (_) => _.vendor.business_name);
      const addressFields = ['street_address', 'suburb', 'state', 'postcode', 'country'];
      const po_address = addressFields.map(field => safeIdx(po, _ => _[field])).filter(Boolean).join(', ');
      const status = safeIdx(po, (_) => _.status);
      const project = safeIdx(po, (_) => _.project.name);
      const issueDate = po.createdAt ? format(new Date(po.createdAt), "dd/MM/yyyy") : "";
      const total = parseFloat(po.totalCost || 0);
      const is_credit_invoice = safeIdx(po, (_) => _.is_credit_invoice) || false;
      const receive_credit_amount = safeIdx(po, (_) => _.receive_credit_amount) || 0;

      return {
        total,
        vendorName,
        type: safeIdx(po, (_) => _.vendor.type),
        address: po_address || safeIdx(po, (_) => _.vendor.address.replace(/ /g, ', ')),
        project,
        issueDate,
        id: po.id,
        f_id: po.f_id,
        poId: po.u_id,
        vendorId: safeIdx(po, (_) => _.vendor.id),
        status,
        projectId: po.projectId,
        materialstatus: safeIdx(po, (_) => _.materialstatus),
        invoicestatus: safeIdx(po, (_) => _.invoicestatus),
        is_credit_invoice,
        receive_credit_amount,
        deliveryDate: po.deliveryDate,
        userName: po.userName,
      };
    });
  }, []);

  const formatWorkOrderData = useCallback((orders) => {
    return orders.map((wo) => {
      const vendorName = safeIdx(wo, (_) => _.vendor.business_name);
      const addressFields = ['street_address', 'suburb', 'state', 'postcode', 'country'];
      const wo_address = addressFields.map(field => safeIdx(wo, _ => _[field])).filter(Boolean).join(', ');
      const status = safeIdx(wo, (_) => _.status);
      const project = safeIdx(wo, (_) => _.project.name);
      const issueDate = wo.createdAt ? format(new Date(wo.createdAt), "dd/MM/yyyy") : "";
      const total = parseFloat(wo.woTotal || wo.totalCost || 0);

      return {
        total,
        amountClaimed: parseFloat(wo.amountClaimed || 0),
        retention: parseFloat(wo.retention || 0),
        approved: parseFloat(wo.approved || 0),
        vendorName,
        type: safeIdx(wo, (_) => _.vendor.type),
        address: wo_address || safeIdx(wo, (_) => _.vendor.address.replace(/ /g, ', ')),
        project,
        issueDate,
        id: wo.id,
        f_id: wo.f_id,
        poId: wo.u_id,
        projectId: wo.projectId,
        vendorId: safeIdx(wo, (_) => _.vendor.id),
        status,
        invoicestatus: safeIdx(wo, (_) => _.invoicestatus),
      };
    });
  }, []);

  // --- API Callbacks (Count Loaders) ---

  const loadPOCounts = useCallback(async (projectId) => {
    if (!rSupplierPermission) return;
    const selectedProjectId = projectId !== 'all' ? projectId : '';
    try {
      // NOTE: getPurchaseOrderTabCounts is a mock function as the actual file is external.
      const countResult = await dispatch(getPurchaseOrderTabCounts({
        vendorType: 'Supplier',
        projectId: selectedProjectId,
        search,
        vendorOrdersPage: true
      }));
      const counts = { active: countResult?.active ?? 0, inactive: countResult?.inactive ?? 0 };
      // Use functional update to avoid depending on poData in this callback
      setPoData(prev => ({ ...prev, counts }));
    } catch (error) {
      console.error('Error loading purchase order tab counts:', error);
      setPoData(prev => ({ ...prev, counts: { active: 0, inactive: 0 } }));
    }
  }, [dispatch, rSupplierPermission, search]); // Stable dependencies: dispatch, permissions, search

  const loadWOCounts = useCallback(async (projectId) => {
    if (!rContractorPermission) return;
    const selectedProjectId = projectId !== 'all' ? projectId : '';
    try {
      // NOTE: getWorkOrderTabCounts is a mock function as the actual file is external.
      const countResult = await dispatch(getWorkOrderTabCounts({ projectId: selectedProjectId, search, vendorOrdersPage: true }));
      const counts = { active: countResult?.active ?? 0, inactive: countResult?.inactive ?? 0 };
      // Use functional update to avoid depending on woData in this callback
      setWoData(prev => ({ ...prev, counts }));
    } catch (error) {
      console.error('Error loading work order tab counts:', error);
      setWoData(prev => ({ ...prev, counts: { active: 0, inactive: 0 } }));
    }
  }, [dispatch, rContractorPermission, search]); // Stable dependencies: dispatch, permissions, search

  // --- API Callbacks (List Loaders) ---

  const loadProjects = useCallback(async () => {
    try {
      // NOTE: getProjectList is a mock function as the actual file is external.
      let projects = await dispatch(getProjectList(null, 'PROJECT'));
      let newProjectsList = [{ key: "All Projects", value: "all" }];
      Array.isArray(projects) && projects.forEach((project) => {
        if (project && project.name && project.id) {
          newProjectsList.push({ key: project.name, value: project.id });
        }
      });
      newProjectsList.sort((a, b) => a.key.toLowerCase().localeCompare(b.key.toLowerCase()));
      setProjectsList(newProjectsList);
    } catch (e) {
      console.error("Failed to load projects for filter:", e);
    }
  }, [dispatch]);

  const loadPOData = useCallback(async (projectId) => {
    if (!rSupplierPermission) return;
    setIsLoading(true);
    const selectedProjectId = projectId !== 'all' ? projectId : '';

    try {
      // NOTE: getPOList is a mock function as the actual file is external.
      const { list } = await dispatch(getPOList({
        vendorType: 'Supplier',
        projectId: selectedProjectId,
        tab: activeSubTab,
        page: currentPage,
        limit,
        search,
        vendorOrdersPage: true
      }));
      const formattedList = formatPurchaseOrderData(list || []);

      // Update *only* the list, preserving current counts via functional update
      setPoData(prev => ({ ...prev, list: formattedList }));
    } catch (error) {
      console.error('Error loading purchase orders:', error);
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, activeSubTab, currentPage, limit, search, formatPurchaseOrderData, rSupplierPermission]);
  // Dependencies: now only rely on inputs to the API call, not the resultant state.

  const loadWOData = useCallback(async (projectId) => {
    if (!rContractorPermission) return;
    setIsLoading(true);
    const selectedProjectId = projectId !== 'all' ? projectId : '';

    try {
      // NOTE: getPOList is a mock function as the actual file is external.
      const { list } = await dispatch(getPOList({
        vendorType: 'sub-contractor',
        projectId: selectedProjectId,
        tab: activeSubTab, // CRITICAL: Use the current subtab state
        page: currentPage,
        limit,
        search,
        vendorOrdersPage: true
      }));
      const formattedList = formatWorkOrderData(list || []);

      // Update *only* the list, preserving current counts via functional update
      setWoData(prev => ({ ...prev, list: formattedList }));
    } catch (error) {
      console.error('Error loading work orders:', error);
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, activeSubTab, currentPage, limit, search, formatWorkOrderData, rContractorPermission]);
  // Dependencies: now only rely on inputs to the API call, not the resultant state.

  // --- Combined Loaders for PayInvoice callback ---
  const refreshPODataAndCounts = useCallback(async () => {
    const projectIdToUse = selectedProject;
    await Promise.all([
      loadPOData(projectIdToUse),
      loadPOCounts(projectIdToUse)
    ]);
  }, [loadPOData, loadPOCounts, selectedProject]);

  const refreshWODataAndCounts = useCallback(async () => {
    const projectIdToUse = selectedProject;
    await Promise.all([
      loadWOData(projectIdToUse),
      loadWOCounts(projectIdToUse)
    ]);
  }, [loadWOData, loadWOCounts, selectedProject]);


  // --- Main Effect 1: Triggers for Tab/Filter/Search changes (reloads counts and list) ---
  useEffect(() => {
    // 1. Initial URL Sync (must happen first)
    const urlState = getTabState();
    if (urlState.activeTab !== activeTab || urlState.activeSubTab !== activeSubTab) {
      setActiveTab(urlState.activeTab);
      setActiveSubTab(urlState.activeSubTab);
      return;
    }

    // 2. Load Filter Options (must happen on most changes to ensure filter stability)
    loadProjects();

    // 3. Load Counts & List
    const projectIdToUse = selectedProject; // Use selectedProject directly as it's a dependency

    if (activeTab === VENDOR_TAB.PURCHASE_ORDERS && rSupplierPermission) {
      loadPOCounts(projectIdToUse);
      loadPOData(projectIdToUse);
    } else if (activeTab === VENDOR_TAB.WORK_ORDERS && rContractorPermission) {
      loadWOCounts(projectIdToUse);
      loadWOData(projectIdToUse);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeTab, activeSubTab, selectedProject, search, rSupplierPermission, rContractorPermission,
    // Dependency functions from useCallbacks
    loadProjects, loadPOData, loadWOData, loadPOCounts, loadWOCounts, getTabState
  ]);

  // --- Main Effect 2: Triggers solely for Pagination/Limit changes (reloads list only) ---
  useEffect(() => {
    // Prevent initial run from causing double fetch (data is fetched by Effect 1 when currentPage=1, limit=DEFAULT)
    if (currentPage === 1 && limit === PerPageCount.DEFAULT) {
      return;
    }

    const projectIdToUse = selectedProject; // Use selectedProject directly

    if (activeTab === VENDOR_TAB.PURCHASE_ORDERS && rSupplierPermission) {
      loadPOData(projectIdToUse);
    } else if (activeTab === VENDOR_TAB.WORK_ORDERS && rContractorPermission) {
      loadWOData(projectIdToUse);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, limit, activeTab]);

  // --- Filter Handlers ---

  const poFilterHandler = useCallback(() => {
    // Toggling the filter dialog
    setDialogState(prev => ({
      ...prev,
      showFilter: !prev.showFilter,
    }));
  }, []);

  const changeFilter = useCallback((projectId) => {
    const newProject = projectId || 'all';
    poFilterHandler(); // Close dialog
    if (newProject !== selectedProject) {
      setSelectedProject(newProject);
      setCurrentPage(1); // Reset page when filter changes
    }
  }, [poFilterHandler, selectedProject]);

  // --- Action Handlers (Modals/Navigation) ---

  const handleMenu = useCallback((rowData) => {
    setDialogState(prev => ({
      ...prev,
      poId: rowData.poId,
      woNo: rowData.f_id || rowData.id,
      invoiceData: {
        poNumber: rowData.poId,
        poNo: rowData.f_id || rowData.id,
        isSubContractor: rowData.type === "sub-contractor",
        status: rowData.status,
        projectId: rowData.projectId,
        vendorId: rowData.vendorId,
      },
    }));
  }, []);

  const cancelDialogState = useCallback((id = null) => {
    setDialogState(prev => ({
      ...prev,
      openCancelDialog: !prev.openCancelDialog,
      poId: id || prev.poId
    }));
  }, []);

  const reactivateDialogState = useCallback((id = null) => {
    setDialogState(prev => ({
      ...prev,
      openReactivateDialog: !prev.openReactivateDialog,
      poId: id || prev.poId
    }));
  }, []);

  const confirmReactivateWO = useCallback(async () => {
    const { poId } = dialogState;
    const res = await dispatch(reactivateWorkOrder(poId));
    if (res.status === 'SUCCESS') {
      reactivateDialogState();
      refreshWODataAndCounts();
    }
  }, [dialogState, dispatch, refreshWODataAndCounts, reactivateDialogState]);

  const confirmCancelWO = useCallback(async () => {
    const { poId } = dialogState;
    const res = await dispatch(cancelWorkOrder(poId));
    if (res.status === 'SUCCESS') {
      cancelDialogState();
      refreshWODataAndCounts();
    }
  }, [dialogState, dispatch, refreshWODataAndCounts, cancelDialogState]);

  const viewOrder = useCallback(() => {
    const { poId } = dialogState;
    const workType = "project";
    const subTabQuery = `&returnSubTab=${activeSubTab}`;
    const routePath = activeTab === VENDOR_TAB.PURCHASE_ORDERS
      ? `/${VENDOR}/${VENDOR_ORDERS}/purchase-order/view/${poId}/${workType}?returnTab=${activeTab}${subTabQuery}`
      : `/${VENDOR}/${VENDOR_ORDERS}/work-order/view/${poId}/${workType}?returnTab=${activeTab}${subTabQuery}`;
    navigate(routePath);
  }, [dialogState, activeTab, activeSubTab, navigate]);

  const editOrder = useCallback(() => {
    const { poId } = dialogState;
    const workType = "project";
    const subTabQuery = `&returnSubTab=${activeSubTab}`;
    const routePath = activeTab === VENDOR_TAB.PURCHASE_ORDERS
      ? `/${VENDOR}/${VENDOR_ORDERS}/purchase-order/edit/${poId}/${workType}?returnTab=${activeTab}${subTabQuery}`
      : `/${VENDOR}/${VENDOR_ORDERS}/work-order/edit/${poId}/${workType}?returnTab=${activeTab}${subTabQuery}`;
    navigate(routePath);
  }, [dialogState, activeTab, activeSubTab, navigate]);

  const deleteDialogState = useCallback(() => {
    setDialogState(prev => ({ ...prev, openDeleteDialog: !prev.openDeleteDialog }));
  }, []);

  const deleteHandler = useCallback(async () => {
    const { poId } = dialogState;
    const orderType = activeTab === VENDOR_TAB.PURCHASE_ORDERS ? "Purchase Order" : "Work Order";
    // NOTE: deletePurchaseOrder is a mock function as the actual file is external.
    await dispatch(deletePurchaseOrder(poId, `${orderType} Deleted successfully`));
    deleteDialogState();
    // Re-trigger data load and count load
    if (activeTab === VENDOR_TAB.PURCHASE_ORDERS) {
      refreshPODataAndCounts();
    } else {
      refreshWODataAndCounts();
    }
  }, [dialogState, activeTab, dispatch, deleteDialogState, refreshPODataAndCounts, refreshWODataAndCounts]);

  const handlePayInvoiceModal = useCallback((status, allProjects = false) => {
    setDialogState(prev => ({
      ...prev,
      showPayInvoice: status,
      invoiceData: { ...(allProjects ? {} : { ...prev.invoiceData }) },
    }));
  }, []);

  const viewInvoices = useCallback(() => {
    const { poId } = dialogState;
    const invoiceTab = activeTab === VENDOR_TAB.PURCHASE_ORDERS ? "supplier-invoices" : "contractor-invoices";
    navigate(`/${VENDOR}/${VENDOR_INVOICES}?tab=${invoiceTab}&poId=${poId}&type=project`);
  }, [dialogState, activeTab, navigate]);

  const gettemplate = useCallback(async (type) => {
    let newdata = [];
    const templateData = await dispatch(getGlobalSettingTemplateTypes(type, null));
    if (Array.isArray(templateData)) {
      templateData.forEach((item) => {
        newdata.push({
          value: item.id,
          key: item.name,
        });
      });
    }
    setDialogState(prev => ({
      ...prev,
      templateOptions: newdata
    }));
  }, [dispatch]);

  const handleDownloadDialog = useCallback((id = "", templateType = "", overrideData = null) => {
    setDialogState(prev => {
      const currentId = id && (typeof id === 'string' || typeof id === 'number') ? id : prev.poId;
      const isOpening = !prev.downloadData.open;
      const newDownloadData = { ...prev.downloadData, open: isOpening, id: currentId || "", overrideData };

      if (isOpening) {
        const type = templateType || (activeTab === VENDOR_TAB.PURCHASE_ORDERS ? 'purchase_order' : 'work_order');
        gettemplate(type);
      }

      return {
        ...prev,
        downloadData: newDownloadData
      };
    });
  }, [activeTab, gettemplate]);

  const downloadFile = useCallback(async (fileFormat = "pdf", tempId) => {
    const { downloadData } = dialogState;
    const orderType = activeTab === VENDOR_TAB.PURCHASE_ORDERS ? "Purchase Order" : "Work Order";

    try {
      if (fileFormat === "pdf" || fileFormat === "ALL") {
        await dispatch(downloadProjectPO(downloadData.id, fileFormat, tempId, undefined, downloadData.overrideData))
          .catch(() => dispatch(errorSnackBar(`Failed to download PDF ${orderType}`)));
      }
      if (fileFormat === "docx" || fileFormat === "ALL") {
        await dispatch(downloadProjectPO(downloadData.id, fileFormat, tempId, undefined, downloadData.overrideData))
          .catch(() => dispatch(errorSnackBar(`Failed to download DOCX ${orderType}`)));
      }
      handleDownloadDialog();
    } catch (error) {
      dispatch(errorSnackBar(`An error occurred while downloading the ${orderType}`));
    }
  }, [dialogState, activeTab, dispatch, handleDownloadDialog]);

  // --- Table Configuration ---

  const purchaseOrderColumns = useMemo(() => ([
    {
      title: "PO Number",
      field: "id",
      dataCy: "po-no",
      render: (rowData) => (
        <Link to={`/${VENDOR}/${VENDOR_ORDERS}/purchase-order/view/${rowData.poId}/project?returnTab=${activeTab}&returnSubTab=${activeSubTab}`}>
          {rowData.f_id || rowData.id}
        </Link>
      ),
    },
    { title: "Vendor", field: "vendorName", dataCy: "vendor-name" },
    { title: "Invoice Type", field: "invoiceType", render: (rowData) => rowData.is_credit_invoice ? "Credit" : "Regular" },
    { title: "Ordered By", field: "userName", dataCy: "ordered-by" },
    {
      title: "Delivery Date",
      field: "deliveryDate",
      render: (rowData) => {
        const today = moment();
        const deliveryDate = moment(rowData.deliveryDate);
        const isAfterDeliveryDate = today.isAfter(deliveryDate, 'day');
        const isNotFullyReceived = rowData.materialstatus !== "Fully Received";
        const isOverdue = isAfterDeliveryDate && isNotFullyReceived;
        const formattedDate = rowData.deliveryDate ? moment(rowData.deliveryDate).format('MMM DD, YYYY') : 'Not Specified';
        return isOverdue ?
          <span className="text-danger" data-cy="overdue-delivery-date">{formattedDate}</span>
          : <span data-cy="normal-delivery-date">{formattedDate}</span>
      }
    },
    { title: "Project", field: "project", dataCy: "project" },
    { title: "Issue Date", field: "issueDate", dataCy: "issue-date" },
    {
      title: "Total",
      field: "total",
      class: 'text-right',
      render: (rowData) => {
        const amount = rowData.is_credit_invoice
          ? (rowData.receive_credit_amount ?? rowData.total)
          : rowData.total;
        return `${numberFormat(amount)}`;
      },
      dataCy: "total",
    },
    {
      title: "Status",
      field: "status",
      render: (rowData) => {
        if (rowData.is_credit_invoice) return '';
        const statusClass = rowData.status ? rowData.status.toLowerCase().replace(/ /g, '-') : '';
        return (
          <span className={`status-badge status-${statusClass}`}>
            {rowData.status}
          </span>
        );
      },
      dataCy: "status",
    },
  ]), [activeTab, activeSubTab]);

  const workOrderColumns = useMemo(() => ([
    {
      title: "WO Number",
      field: "id",
      dataCy: "wo-no",
      render: (rowData) => (
        <Link to={`/${VENDOR}/${VENDOR_ORDERS}/work-order/view/${rowData.poId}/project?returnTab=${activeTab}&returnSubTab=${activeSubTab}`}>
          {rowData.f_id || rowData.id}
        </Link>
      ),
    },
    { title: "Vendor", field: "vendorName", dataCy: "vendor-name" },
    { title: "Project", field: "project", dataCy: "project" },
    {
      title: "WO Total",
      field: "total",
      class: 'text-right',
      render: (rowData) => `${numberFormat(rowData.total)}`,
      dataCy: "wo-total",
    },
    {
      title: "Amount Claimed",
      field: "amountClaimed",
      class: 'text-right',
      render: (rowData) => `${numberFormat(rowData.amountClaimed)}`,
      dataCy: "amount-claimed",
    },
    {
      title: "Retention",
      field: "retention",
      class: 'text-right',
      render: (rowData) => `${numberFormat(rowData.retention)}`,
      dataCy: "retention",
    },
    {
      title: "Approved",
      field: "approved",
      class: 'text-right',
      render: (rowData) => `${numberFormat(rowData.approved)}`,
      dataCy: "approved",
    },
    { title: "Issue Date", field: "issueDate", dataCy: "issue-date" },
    {
      title: "Status",
      field: "status",
      render: (rowData) => {
        const statusClass = rowData.status ? rowData.status.toLowerCase().replace(/ /g, '-') : '';
        return (
          <span className={`status-badge status-${statusClass}`}>
            {rowData.status}
          </span>
        );
      },
      dataCy: "status",
    },
  ]), [activeTab, activeSubTab]);

  const renderMenu = useCallback((data) => {
    const isPO = activeTab === VENDOR_TAB.PURCHASE_ORDERS;
    return (
      <Popover id="popover-basic" data-cy={isPO ? "po-list-table-menu" : "wo-list-table-menu"}>
        <Popover.Body>
          <ul className="action-block-list">
            {isPO ? (
              // Purchase Order Actions
              <>
                <li onClick={viewOrder} data-cy="view-po">View PO</li>
                {!data.is_credit_invoice && writePermission && data && data.status === "Pending" && (
                  <li onClick={editOrder} data-cy="edit-po">Edit PO</li>
                )}
                <li onClick={() => handleDownloadDialog(data.poId, 'purchase_order')} data-cy="download-po">Download PO</li>
                {!data.is_credit_invoice && writePermission && data.materialstatus !== "Fully Received" && (
                  <li
                    className={!(entitlements.receiveStock) && 'disabled'}
                    onClick={() => (entitlements.receiveStock) && (() =>
                      navigate(`/${PROJECT_MANAGEMENT}/receive-stock/${data.projectId}/${data.vendorId}/${data.poId}?returnTab=${activeTab}&returnSubTab=${activeSubTab}`)
                    )()}
                    data-cy="receive-stock"
                  >
                    Receive Stock
                  </li>
                )}
                {!data.is_credit_invoice && writePermission && data.invoicestatus !== "Fully Invoiced" && (
                  <li onClick={() => handlePayInvoiceModal(true)} data-cy="receive-invoice">Receive Invoice</li>
                )}
                <li onClick={viewInvoices} data-cy="view-invoices">View Invoices</li>
                {writePermission && data.status === "Pending" && (
                  <li onClick={deleteDialogState} data-cy="delete-po">Delete</li>
                )}
              </>
            ) : (
              // Work Order Actions
              <>
                <li onClick={viewOrder} data-cy="view-wo">View WO</li>
                <li onClick={() => handleDownloadDialog(data.poId, 'work_order')} data-cy="download-wo">Download WO</li>
                {writePermission && (data.status !== "Finished") && (
                  <li onClick={editOrder} data-cy="edit-wo">Edit WO</li>
                )}
                {writePermission && (
                  <li onClick={() => handlePayInvoiceModal(true)} data-cy="receive-invoice">Receive Invoice</li>
                )}
                {writePermission && (
                  <li onClick={viewInvoices} data-cy="view-invoices">View Invoices</li>
                )}
                {writePermission && (data.status === "Approved" || data.status === "Active" || data.status === "Pending") && (
                  data.invoicestatus === "Not Invoiced" ? (
                    <li onClick={() => cancelDialogState(data.poId)} data-cy="cancel-wo">Cancel</li>
                  ) : (data.invoicestatus === "Partially Invoiced" || data.invoicestatus === "Fully Invoiced") ? (
                    <li className="disabled" data-cy="cancel-wo-disabled">
                      <span title="Cannot cancel a partially or fully invoiced Work Order">Cancel</span>
                    </li>
                  ) : <li onClick={() => cancelDialogState(data.poId)} data-cy="cancel-wo">Cancel</li>
                )}
                {writePermission && (data.status === "Pending" || (data.status === "Approved" && data.invoicestatus === "Not Invoiced")) && (
                  <li onClick={deleteDialogState} data-cy="delete-wo">Delete</li>
                )}
                {writePermission && (data.status === "Cancelled") && (
                  <li onClick={() => reactivateDialogState(data.poId)} data-cy="reactivate-wo">Reactivate</li>
                )}
              </>
            )}
          </ul>
        </Popover.Body>
      </Popover>
    );
  }, [activeTab, activeSubTab, writePermission, entitlements, navigate, viewOrder, editOrder, handleDownloadDialog, handlePayInvoiceModal, viewInvoices, deleteDialogState, cancelDialogState, reactivateDialogState]);


  const activeListData = activeTab === VENDOR_TAB.PURCHASE_ORDERS ? poData.list : woData.list;
  const activeTabCounts = activeTab === VENDOR_TAB.PURCHASE_ORDERS ? poData.counts : woData.counts;

  return (
    <>
      {/* Modal Components */}
      <DeleteDialog
        open={dialogState.openDeleteDialog}
        id={dialogState.poId}
        deleteHandler={deleteHandler}
        dialogClose={deleteDialogState}
        moduleName={activeTab === VENDOR_TAB.PURCHASE_ORDERS ? "Purchase Order" : "Work Order"}
        description={`${activeTab === VENDOR_TAB.PURCHASE_ORDERS ? "Purchase Order" : "Work Order"} - ${dialogState.woNo}`}
        dataCy={activeTab === VENDOR_TAB.PURCHASE_ORDERS ? "delete-po-dialog" : "delete-wo-dialog"}
      />
      <Modal
        open={dialogState.openCancelDialog}
        onClose={cancelDialogState}
        fullWidth={true}
        maxWidth='sm'
        modalHeading="Cancel Work Order"
        modalSaveTxt={<span data-cy="confirm-cancel">Yes</span>}
        modalCloseTxt='No'
        onClick={confirmCancelWO}
        showActionBtn
        savebtnClass='createBtn'
        cxlbtnClass='cancelBtn'
        primary
        dataCy="cancel-wo-dialog"
      >
        <p>Are you sure you want to cancel Work Order - {dialogState.woNo}? </p>
      </Modal>
      <Modal
        open={dialogState.openReactivateDialog}
        onClose={reactivateDialogState}
        fullWidth={true}
        maxWidth='sm'
        modalHeading="Reactivate Work Order"
        modalSaveTxt={<span data-cy="confirm-reactivate">Yes</span>}
        modalCloseTxt='No'
        onClick={confirmReactivateWO}
        showActionBtn
        savebtnClass='createBtn'
        cxlbtnClass='cancelBtn'
        primary
        dataCy="reactivate-wo-dialog"
      >
        <p>Are you sure you want to reactivate Work Order - {dialogState.woNo}? </p>
      </Modal>
      <DownloadDialog
        {...(dialogState.downloadData || {})}
        downloadFile={downloadFile}
        templateOptions={dialogState.templateOptions}
        handleDownloadDialog={handleDownloadDialog}
        downloadModuleName={activeTab === VENDOR_TAB.PURCHASE_ORDERS ? "Purchase Order" : "Work Order"}
        dataCy={activeTab === VENDOR_TAB.PURCHASE_ORDERS ? "download-po-dialog" : "download-wo-dialog"}
      />
      {dialogState.showPayInvoice && (
        <PayInvoice
          {...(dialogState.invoiceData || {})}
          poId={dialogState.poId}
          workType={"project"}
          handleDownloadDialog={handleDownloadDialog}
          handleClose={() => handlePayInvoiceModal(false)}
          getPOList={activeTab === VENDOR_TAB.PURCHASE_ORDERS ? refreshPODataAndCounts : refreshWODataAndCounts}
          dataCy={activeTab === VENDOR_TAB.PURCHASE_ORDERS ? "receive-invoice-po-modal" : "receive-invoice-wo-modal"}
        />
      )}

      {/* Filter Modals */}
      {dialogState.showFilter && projectModule && (
        activeTab === VENDOR_TAB.PURCHASE_ORDERS ? (
          <POFilter
            projectsList={projectsList}
            changeFilter={changeFilter}
            workType={"project"}
            open={dialogState.showFilter}
            dialogClose={poFilterHandler}
            selectedProject={selectedProject}
            dataCy="po-list-filter"
          />
        ) : (
          <WOFilter
            projectsList={projectsList}
            changeFilter={changeFilter}
            workType={"project"}
            open={dialogState.showFilter}
            dialogClose={poFilterHandler}
            selectedProject={selectedProject}
            dataCy="wo-list-filter"
          />
        )
      )}

      {!projectModule ? (
        <p>
          You do not currently have access to any Projects Finance. Please
          contact your Administrator if this is required.
        </p>
      ) : (
        <div className="purchase-order-block vendor-management" data-cy="vendor-orders">
          <div className="row">
            <div className="col-12">
              <h2 className="content-heading text-uppercase">ORDERS</h2>
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-12">
              <div className="vendor-header">
                <Tabs
                  activeKey={activeTab}
                  id="vendor-tabs"
                  className="vendor-type-tabs mb-0"
                  onSelect={handleMainTabChange}
                >
                  {rSupplierPermission && (
                    <Tab eventKey={VENDOR_TAB.PURCHASE_ORDERS} title={<span data-cy="purchase-orders-tab">PURCHASE ORDERS</span>} />
                  )}
                  {rContractorPermission && (
                    <Tab eventKey={VENDOR_TAB.WORK_ORDERS} title={<span data-cy="work-orders-tab">WORK ORDERS</span>} />
                  )}
                </Tabs>

                <div className="w-auto ms-auto mt-auto d-flex align-items-center gap-2">
                  {activeTab === VENDOR_TAB.PURCHASE_ORDERS && rSupplierPermission && writePermission && (
                    <Link
                      to={`/${VENDOR}/${VENDOR_ORDERS}/purchase-order/create?returnTab=${activeTab}&returnSubTab=${activeSubTab}`}
                      className="secondarybtn secondarybtn-outline btn-ch ms-auto me-2 btn btn-primary"
                      data-cy="create-po"
                    >
                      CREATE PO
                    </Link>
                  )}
                  {activeTab === VENDOR_TAB.WORK_ORDERS && rContractorPermission && writePermission && (
                    <Link
                      to={`/${VENDOR}/${VENDOR_ORDERS}/work-order/create?returnTab=${activeTab}&returnSubTab=${activeSubTab}`}
                      className="secondarybtn secondarybtn-outline btn-ch ms-auto me-2 btn btn-primary"
                      data-cy="create-wo"
                    >
                      CREATE WO
                    </Link>
                  )}
                  <span onClick={poFilterHandler} className="filter-icon" data-cy="filter-icon"><FilterAltIcon /></span>
                </div>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-12">
              {(activeTab === VENDOR_TAB.PURCHASE_ORDERS && rSupplierPermission) || (activeTab === VENDOR_TAB.WORK_ORDERS && rContractorPermission) ? (
                <>
                  <Tabs
                    activeKey={activeSubTab}
                    id="order-sub-tabs"
                    className="mb-3 sub-tabs"
                    onSelect={handleSubTabChange}
                  >
                    <Tab eventKey="active" title={`Active (${activeTabCounts.active})`} tabClassName="active-tab" />
                    <Tab eventKey="inactive" title={`Inactive (${activeTabCounts.inactive})`} tabClassName="inactive-tab" />
                  </Tabs>

                  <CustomTable
                    columns={activeTab === VENDOR_TAB.PURCHASE_ORDERS ? purchaseOrderColumns : workOrderColumns}
                    data={activeListData}
                    isAction={true}
                    handleMenu={handleMenu}
                    dataCy={activeTab === VENDOR_TAB.PURCHASE_ORDERS ? `supplier-po-list-table-${activeSubTab}` : `contractor-wo-list-table-${activeSubTab}`}
                    paginationCount={activeSubTab === 'active' ? activeTabCounts.active : activeTabCounts.inactive}
                    currentPage={currentPage}
                    pageHandler={pageHandler}
                    limitChangeHandler={limitChangeHandler}
                    itemsPerPage={limit}
                    searchHandler={searchHandler}
                    isLoading={isLoading}
                    externalSearchValue={search}
                    externalSearchHandler={searchHandler}
                    menu={renderMenu}
                    fileName={`${activeTab === VENDOR_TAB.PURCHASE_ORDERS ? "Purchase Orders" : "Work Orders"} List`}
                  />
                </>
              ) : (
                <p>You do not have permission to view this order type.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default VendorOrders;
